"use client";

import { type ReactNode, type Ref, useMemo, useState } from "react";

import { cn } from "../../utils";
import { type BreadcrumbData, LayoutContextProvider, useLayoutContext } from "./context";

export interface LayoutRootProps {
  children: ReactNode;
  className?: string;
  ref?: Ref<HTMLDivElement>;
}

const TOP_BANNER_HEIGHT = 46;

const Root = ({ ref, children, className }: LayoutRootProps) => {
  const parentContext = useLayoutContext();
  const { banner } = parentContext;

  // Breadcrumb state lives here rather than in Layout.Provider so it is scoped to one app shell.
  // Each app mounts its own Root, so switching apps drops the previous app's breadcrumb instead of
  // leaving it next to the new app's sidebar while the new page streams in.
  const [breadcrumbData, setBreadcrumbData] = useState<BreadcrumbData | undefined>();

  const contextValue = useMemo(
    () => ({ ...parentContext, breadcrumbData, setBreadcrumbData }),
    [parentContext, breadcrumbData]
  );

  return (
    <LayoutContextProvider value={contextValue}>
      <main
        className={cn("relative flex w-screen flex-col overflow-hidden bg-neutral-98", className)}
        style={{
          height: banner
            ? `calc(100vh - ${TOP_BANNER_HEIGHT}px - env(safe-area-inset-bottom) * 2)`
            : `calc(100vh - env(safe-area-inset-bottom) * 2)`,
        }}
        ref={ref}
      >
        {children}
      </main>
    </LayoutContextProvider>
  );
};

Root.displayName = "LayoutRoot";

export default Root;
